import { Group, Menu, Text, UnstyledButton } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { ReactElement } from 'react';
import { DateRangeFilter } from '@/pages/PatientCentral/shared/DateRangeFilter';
import { LIST_RANGE_PRESETS, ListRange, ListRangePresetId } from './listRange';
import { QUEUE_FILTER_BUTTON_CLASS, QUEUE_FILTER_BUTTON_STYLE, QUEUE_FILTER_TEXT_PROPS } from './queueFilterStyle';

/**
 * The queue's date scope — a control, not a chip.
 *
 * It sits left of the count and reads as a scope rather than one more thing
 * switched on, because it bounds the tab before any chip runs. The custom date
 * inputs reuse `DateRangeFilter` rather than introducing a second date UI.
 */
export interface ListRangeControlProps {
  presetId: ListRangePresetId;
  value: ListRange;
  /**
   * The preset chosen, plus the dates typed when it is `custom`. A preset's
   * range is the owner's to derive — from a clock it keeps current — not this
   * control's to compute once at click time.
   */
  onChange: (presetId: ListRangePresetId, customRange?: ListRange) => void;
  /** What the date means on this tab: `Signed up`, `Consult`, `Won`. */
  label?: string;
  /** The tab's own presets; only the date varies between tabs. */
  presets?: Array<{ id: ListRangePresetId; label: string }>;
}

const ISO_DATE_FORMAT = 'YYYY-MM-DD';

export function ListRangeControl({
  presetId,
  value,
  onChange,
  label = 'Range',
  presets = LIST_RANGE_PRESETS,
}: ListRangeControlProps): ReactElement {
  const activeLabel = presets.find((preset) => preset.id === presetId)?.label ?? 'Custom range';

  return (
    <Group gap="xs" wrap="nowrap">
      {/*
        duration 0 is deliberate, not a leftover. A five-item scope menu gains
        nothing from an animation, and under jsdom the default transition never
        commits the dropdown to the DOM — so removing this makes the two
        preset-selection tests fail with a confusing "unable to find element".
      */}
      <Menu position="bottom-start" withinPortal transitionProps={{ duration: 0 }}>
        <Menu.Target>
          <UnstyledButton
            aria-label={`${label} · ${activeLabel}`}
            className={QUEUE_FILTER_BUTTON_CLASS}
            style={QUEUE_FILTER_BUTTON_STYLE}
          >
            <Group gap={6} wrap="nowrap">
              <Text {...QUEUE_FILTER_TEXT_PROPS}>{`${label} · ${activeLabel}`}</Text>
              <IconChevronDown size={11} stroke={2} color="rgba(0, 0, 0, 0.55)" />
            </Group>
          </UnstyledButton>
        </Menu.Target>
        <Menu.Dropdown>
          {presets.map((preset) => (
            <Menu.Item key={preset.id} fw={preset.id === presetId ? 600 : 400} onClick={() => onChange(preset.id)}>
              {preset.label}
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>

      {presetId === 'custom' ? (
        <DateRangeFilter
          size="xs"
          value={[value.from ? dayjs(value.from).toDate() : null, value.to ? dayjs(value.to).toDate() : null]}
          onChange={([from, to]) =>
            onChange('custom', {
              from: from ? dayjs(from).format(ISO_DATE_FORMAT) : undefined,
              to: to ? dayjs(to).format(ISO_DATE_FORMAT) : undefined,
            })
          }
        />
      ) : null}
    </Group>
  );
}
